import React, { Activity, useCallback, use, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { ChipList } from '@progress/kendo-react-buttons';
import cn from 'clsx';
import { delay } from 'msw';
import { CommentsContext } from './comments-context';
import styles from './styles.module.scss';
import Button from '@components-clean/core/button';
import useCommentMutations from '@components-clean/core/comments-side-bar/comment-mutations';
import useCommentQueries from '@components-clean/core/comments-side-bar/comment-queries';
import useCommentsMessageHub from '@components-clean/core/comments-side-bar/comments-message-hub';
import CommentCard, { Comment } from '@components-clean/core/comments-side-bar/components/cards/comment-card';
import { Reply } from '@components-clean/core/comments-side-bar/components/cards/reply-details';
import CommentForm from '@components-clean/core/comments-side-bar/components/comment-form';
import { mapCommentResponseToComment } from '@components-clean/core/comments-side-bar/utilities';
import ErrorMessage from '@components-clean/core/error';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import { ReadOnlyContext } from '@contexts-clean/read-only-context';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { busDispatch } from '@utils-clean/eventBus';
import { SelectorData } from 'types/graphql-global-types';
import { UserContext } from '@contexts/user-context';
import { ShowError, ShowSuccess, ShowWarning } from '@utils/notification';
import { GET_COMMENTS } from '@queries/common';
import {
    updateComment_OpraMutations_updateComment_CommentWithDeletedReplies as CommentWithDeletedReplies,
    getUpdatedCommentsById_OpraQueries_updatedCommentsById_CommentsWithDeletedItems as CommentsWithDeletedItems,
    getComments,
    getCommentsVariables,
} from '@queries/generated/common.generated';
import { getStudyMembers, getStudyMembersVariables } from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';
import { useTimeout } from '@hooks-clean/use-timeout';

interface CommentCounts {
    open: number;
    closed: number;
    all: number;
}

enum ChipValues {
    Open = 1,
    Closed = 2,
    All = 3,
}

interface CommentFormInput {
    content: string;
}

export interface CommentsSideBarProps {
    parentId: string | undefined;
    entityType: string;
    messageHubRelativeEndpoint: string;
    noParentSelectedMessage: string;
    studyId: string;
    userHasWritePermission: boolean;
}

export const OPEN_COMMENTS_COUNT_CHANGED = 'open-comments-count-changed';

export interface CommentOpenCount {
    parentId: string;
    open: number;
    all: number;
}

export interface IOpenCommentsCountChangedMessage {
    topic: string;
    message: CommentOpenCount;
}

const CommentsSideBarInner = ({
    parentId,
    entityType,
    messageHubRelativeEndpoint,
    noParentSelectedMessage,
    studyId,
    userHasWritePermission,
}: CommentsSideBarProps) => {
    const { t } = useTranslation();
    const [chipValue, setChipValue] = useState<ChipValues>(ChipValues.Open);
    const { user } = use(UserContext);

    // We keep track of comments in a ref as well so never call this set directly, use setComments instead
    const [comments, setComments] = useState<Comment[]>([]);
    const commentsRef = useRef<Comment[]>([]);

    const [waitingOnMessageHubConnecting, setWaitingOnMessageHubConnecting] = useState(true);
    const [commentCounts, setCommentCounts] = useState<CommentCounts>();
    const [showNewCommentForm, setShowNewCommentForm] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const dirtyCommentsRef = useRef<Set<string>>(new Set());
    const shouldPollCommentsRef = useRef<boolean>(false);
    const deletedCommentsRef = useRef<Set<string>>(new Set());
    const deletedRepliesRef = useRef<Set<string>>(new Set());
    const commentsListRef = useRef<HTMLDivElement>(null);

    const scrollTopsByChipRef = useRef<Record<ChipValues, number>>({
        [ChipValues.Open]: 0,
        [ChipValues.Closed]: 0,
        [ChipValues.All]: 0,
    });

    const { getUpdatedComments, getUpdatedCommentsById } = useCommentQueries(parentId, entityType);

    const {
        createComment,
        updateComment,
        openComment,
        closeComment,
        deleteComment,
        createCommentReply,
        updateCommentReply,
        deleteCommentReply,
    } = useCommentMutations(parentId, entityType);

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    // We ref wrap this to pass it through the context for the comment cards to use.
    // We don't want to rerender these every time we show / hide the dialog.
    const confirmDiscardChangesRef = useRef<(dataTestId?: string) => Promise<boolean>>(undefined);
    const { readOnly } = use(ReadOnlyContext);
    const { control, handleSubmit, resetForm, isValid, isDirty: isNewCommentDirty, isSubmitting } = CommentForm('');
    const isNewCommentDirtyRef = useRef(false);
    const isSubmittingRef = useRef(false);
    const hasCommentsEffectRunRef = useRef(false);

    const {
        data: studyMembersData,
        loading: loadingStudyMembers,
        error: errorLoadingStudyMembers,
    } = useQuery<getStudyMembers, getStudyMembersVariables>(GET_STUDY_MEMBERS, {
        variables: { studyId },
    });

    const setCommentsAndRef = (updatedComments: Comment[]) => {
        commentsRef.current = updatedComments;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setComments(updatedComments);
    };

    const updateDirtyState = () =>
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsDirty((isNewCommentDirtyRef.current && !isSubmittingRef.current) || dirtyCommentsRef.current.size > 0);

    const mergeReplies = (existingReplies: Reply[], updatedReplies: Reply[]): Reply[] => {
        const updatedReplyIdsToReplies = new Map(updatedReplies.map((reply) => [reply.id, reply]));

        const mergedReplies = existingReplies.reduce<Reply[]>((accumulator, reply) => {
            const updatedReply = updatedReplyIdsToReplies.get(reply.id);
            updatedReplyIdsToReplies.delete(reply.id);

            updatedReply && updatedReply.version > reply.version
                ? accumulator.push(updatedReply)
                : accumulator.push(reply);

            return accumulator;
        }, []);

        return [
            ...mergedReplies,
            ...updatedReplyIdsToReplies.values().filter((reply) => !deletedRepliesRef.current.has(reply.id)),
        ];
    };

    const updateCommentCollection = (...updateComments: Comment[]) => {
        const updatedCommentIdsToComments = new Map(updateComments.map((comment) => [comment.id, comment]));

        if (
            commentsRef.current.some((comment) => {
                const updatedComment = updatedCommentIdsToComments.get(comment.id);
                return (
                    updatedComment &&
                    !updatedComment.open &&
                    dirtyCommentsRef.current.delete(comment.id) &&
                    updatedComment.updatedById !== user?.id
                );
            })
        ) {
            ShowWarning(t('comments:commentHasBeenClosed'));
        }

        const mergedComments = commentsRef.current.map((comment) => {
            const updatedComment = updatedCommentIdsToComments.get(comment.id);
            if (!updatedComment) {
                return comment;
            }

            updatedCommentIdsToComments.delete(comment.id);
            const replies = mergeReplies(comment.replies, updatedComment.replies);
            return { ...(updatedComment.version > comment.version ? updatedComment : comment), replies };
        });

        setCommentsAndRef([
            ...mergedComments,
            ...updatedCommentIdsToComments.values().filter((comment) => !deletedCommentsRef.current.has(comment.id)),
        ]);

        updateDirtyState();
    };

    const deleteFromCommentCollection = (commentIds: string[], triggeredBySelfMessage?: boolean) => {
        deletedCommentsRef.current = new Set([...deletedCommentsRef.current, ...commentIds]);
        const idsToDelete = new Set(commentIds);

        if (
            commentsRef.current.some(
                (comment) =>
                    !triggeredBySelfMessage && idsToDelete.has(comment.id) && dirtyCommentsRef.current.has(comment.id),
            )
        ) {
            ShowWarning(t('comments:commentHasBeenDeleted'));
        }

        idsToDelete.forEach((id) => dirtyCommentsRef.current.delete(id));
        setCommentsAndRef(commentsRef.current.filter((comment) => !idsToDelete.has(comment.id)));
        updateDirtyState();
    };

    const deleteRepliesFromCommentCollection = (replyIds: string[]) => {
        deletedCommentsRef.current = new Set([...deletedCommentsRef.current, ...replyIds]);
        const idsToDelete = new Set(replyIds);

        const updatedComments = commentsRef.current.map((comment) => {
            const hasMatch = comment.replies.some((reply) => idsToDelete.has(reply.id));
            if (!hasMatch) return comment;

            return {
                ...comment,
                replies: comment.replies.filter((reply) => !idsToDelete.has(reply.id)),
            };
        });

        setCommentsAndRef(updatedComments);
    };

    const getCommentSelectors = (commentSelection: Comment[]) => {
        return commentSelection.map((comment) => {
            return {
                id: comment.id,
                version: comment.version,
                replySelectors: comment.replies.map((reply) => {
                    return {
                        id: reply.id,
                        version: reply.version,
                    };
                }),
            };
        });
    };

    const updateFromCommentsWithDeletedItems = (commentsWithDeletedItems: CommentsWithDeletedItems) => {
        deleteFromCommentCollection(commentsWithDeletedItems.deletedCommentIds);
        deleteRepliesFromCommentCollection(commentsWithDeletedItems.deletedReplyIds);
        updateCommentCollection(
            ...commentsWithDeletedItems.comments.map((comment) => mapCommentResponseToComment(comment)),
        );
    };

    const updateCommentsByIds = async (commentIds: string[]) => {
        const commentIdsSet = new Set(commentIds);
        const existingComments = commentsRef.current.filter((comment) => commentIdsSet.has(comment.id));
        const commentSelectors = getCommentSelectors(existingComments);

        const selectorCommentIds = new Set(commentSelectors.map((selector) => selector.id));
        const newCommentIds = commentIds.filter((id) => !selectorCommentIds.has(id));

        const result = await getUpdatedCommentsById(newCommentIds, commentSelectors);

        if (result) {
            updateFromCommentsWithDeletedItems(result);
            return true;
        }

        return false;
    };

    const {
        connectedAtLeastOnce: connectedToMessageHubAtLeastOnce,
        disconnect: disconnectFromMessageHub,
        startProcessingUpdates,
    } = useCommentsMessageHub({
        messageHubRelativeEndpoint,
        entityType,
        parentId,
        onCommentsUpdated: updateCommentsByIds,
        onCommentDeleted: (isSelfMessage, commentId) => deleteFromCommentCollection([commentId], isSelfMessage),
    });

    const {
        data: commentsData,
        loading: loadingComments,
        error: errorLoadingComments,
    } = useQuery<getComments, getCommentsVariables>(GET_COMMENTS, {
        variables: {
            commentsInput: {
                parentId,
                entityType,
            },
        },
        skip: parentId === undefined || (waitingOnMessageHubConnecting && !connectedToMessageHubAtLeastOnce),
        fetchPolicy: 'no-cache',
    });

    useTimeout(() => setWaitingOnMessageHubConnecting(false), 1000);

    const inactiveOwners = useMemo(() => {
        if (!studyMembersData) {
            return;
        }

        return (
            studyMembersData!.studyMembers
                ?.filter((member) => member!.user && !member!.active)
                .map((member) => member!.user!.id) ?? []
        );
    }, [studyMembersData]);

    const hasError =
        !waitingOnMessageHubConnecting &&
        (errorLoadingComments ||
            errorLoadingStudyMembers ||
            (!loadingComments && !commentsData?.comments) ||
            (!loadingStudyMembers && !studyMembersData?.studyMembers));

    const commentsContextValue = useMemo(
        () => ({
            inactiveOwners,
            userHasWritePermission,
            confirmDiscardChanges: () => confirmDiscardChangesRef.current!(),
        }),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [inactiveOwners],
    );

    const onNewCommentSubmit = async (commentFormInput: CommentFormInput) => {
        const newComment = await createComment(commentFormInput.content);

        if (newComment) {
            ShowSuccess(t('common:actionsSuccessfulToast'));
            resetForm();
            updateCommentCollection(newComment);
            setShowNewCommentForm(false);
            return;
        }

        ShowError(t('errorMessage'));
    };

    const handleUpdateComment = async (
        id: string,
        updateMutation: (replySelectors: SelectorData[]) => Promise<CommentWithDeletedReplies | null>,
    ) => {
        const comment = commentsRef.current.find((x) => x.id === id);
        const replySelectors =
            comment?.replies.map((reply) => {
                return {
                    id: reply.id,
                    version: reply.version,
                };
            }) ?? [];

        const updatedComment = await updateMutation(replySelectors);

        if (updatedComment) {
            const mappedComment = mapCommentResponseToComment(updatedComment.comment);
            updateCommentCollection(mappedComment);
            deleteRepliesFromCommentCollection(updatedComment.deletedReplies as string[]);

            ShowSuccess(t('common:actionsSuccessfulToast'));
            return true;
        }

        ShowError(t('errorMessage'));
        return false;
    };

    const handleDeleteComment = async (commentId: string) => {
        const deleted = await deleteComment(commentId);

        if (deleted) {
            deleteFromCommentCollection([commentId]);
            ShowSuccess(t('common:actionsSuccessfulToast'));
            return;
        }

        ShowError(t('errorMessage'));
    };

    const onNewCommentCancel = async () => {
        if (isNewCommentDirty) {
            if (!(await confirmDiscardChanges())) {
                return;
            }
        }

        resetForm();
        setShowNewCommentForm(false);
        updateDirtyState();
    };

    const updateCommentDirtyState = (commentId: string, isCommentDirty: boolean) => {
        if (isCommentDirty) {
            const comment = commentsRef.current.find((c) => c.id === commentId);
            if (comment && comment.open) {
                dirtyCommentsRef.current.add(commentId);
            }
        } else {
            dirtyCommentsRef.current.delete(commentId);
        }

        updateDirtyState();
    };

    const onCommentDirtyStateUpdated = useCallback(
        (id: string, dirtyState: boolean) => updateCommentDirtyState(id, dirtyState),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    const onUpdateComment = useCallback(
        (id: string, content: string) =>
            handleUpdateComment(id, (replySelectors) => updateComment(id, content, replySelectors)),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    const onCreateCommentReply = useCallback(
        (id: string, content: string) =>
            handleUpdateComment(id, (replySelectors) => createCommentReply(id, content, replySelectors)),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    const onUpdateCommentReply = useCallback(
        (id: string, commentId: string, content: string) =>
            handleUpdateComment(commentId, (replySelectors) => updateCommentReply(id, content, replySelectors)),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    const onDeleteCommentReply = useCallback(
        (id: string, commentId: string) =>
            handleUpdateComment(commentId, (replySelectors) => deleteCommentReply(id, replySelectors)),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    const onOpenComment = useCallback(
        (id: string) => handleUpdateComment(id, (replySelectors) => openComment(id, replySelectors)),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    const onCloseComment = useCallback(
        (id: string) => handleUpdateComment(id, (replySelectors) => closeComment(id, replySelectors)),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [],
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const onDeleteComment = useCallback((id: string) => handleDeleteComment(id), []);

    const getSortedComments = () =>
        comments
            .toSorted((a, b) => (a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0))
            .sort((a, b) => (a.open && !b.open ? -1 : !a.open && b.open ? 1 : 0));

    const pollCommentsRecursive = async () => {
        if (!shouldPollCommentsRef.current) {
            return;
        }

        const result = await getUpdatedComments(getCommentSelectors(commentsRef.current));

        if (!shouldPollCommentsRef.current) {
            return;
        }

        if (result) {
            updateFromCommentsWithDeletedItems(result);
            await delay(30000);
        } else {
            await delay(15000);
        }

        await pollCommentsRecursive();
    };

    const pollComments = async () => {
        await delay(30000);
        await pollCommentsRecursive();
    };

    useEffect(() => {
        isNewCommentDirtyRef.current = isNewCommentDirty;
        isSubmittingRef.current = isSubmitting;
        updateDirtyState();
    }, [isNewCommentDirty, isSubmitting]);

    useEffect(() => {
        if (errorLoadingComments || errorLoadingStudyMembers) {
            disconnectFromMessageHub();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [errorLoadingComments, errorLoadingStudyMembers]);

    useEffect(() => {
        return () => {
            shouldPollCommentsRef.current = false;
            setComponentHasChanges(ComponentNames.commentsSideBar, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        if (connectedToMessageHubAtLeastOnce) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setWaitingOnMessageHubConnecting(false);
        }
    }, [connectedToMessageHubAtLeastOnce]);

    useLayoutEffect(() => {
        if (!commentsData || !commentsData.comments) {
            return;
        }

        setCommentsAndRef(commentsData.comments!.map((comment) => mapCommentResponseToComment(comment!)));
        startProcessingUpdates();

        if (!shouldPollCommentsRef.current) {
            shouldPollCommentsRef.current = true;
            // eslint-disable-next-line no-void
            void pollComments();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [commentsData]);

    useLayoutEffect(() => {
        // Track initial effect run so we don't send an open comment count before comments have been loaded
        if (hasCommentsEffectRunRef.current) {
            const openCommentCount = comments.filter((comment) => comment.open).length;
            if (!commentCounts?.open || commentCounts!.open !== openCommentCount) {
                busDispatch<IOpenCommentsCountChangedMessage>(OPEN_COMMENTS_COUNT_CHANGED, {
                    parentId: parentId!,
                    open: openCommentCount,
                    all: comments.length,
                });
            }

            setCommentCounts({
                open: comments.filter((comment: Comment) => comment.open).length,
                closed: comments.filter((comment: Comment) => !comment.open).length,
                all: comments.length,
            });
        }

        hasCommentsEffectRunRef.current = true;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [comments]);

    useLayoutEffect(() => {
        if (componentHasChanges(ComponentNames.commentsSideBar) !== isDirty) {
            setComponentHasChanges(ComponentNames.commentsSideBar, isDirty);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    useLayoutEffect(() => {
        confirmDiscardChangesRef.current = confirmDiscardChanges;
    }, [confirmDiscardChanges]);

    useLayoutEffect(() => {
        if (commentsListRef.current) {
            commentsListRef.current.scrollTop = scrollTopsByChipRef.current[chipValue];
        }
    }, [chipValue]);

    return (
        <div className={styles.panel}>
            {parentId === undefined ? (
                <InfoField type="Info" message={noParentSelectedMessage} layout="center" />
            ) : hasError ? (
                <ErrorMessage />
            ) : loadingComments || loadingStudyMembers || waitingOnMessageHubConnecting ? (
                <Loading />
            ) : (
                <>
                    <div className={styles.panelHeaderContainer}>
                        <div className={styles.panelHeader}>
                            <div className={styles.panelTitle}>
                                {t('comments:commentsCount', { count: commentCounts?.all })}
                            </div>

                            {!showNewCommentForm ? (
                                <Button
                                    data-testid="create-comment-button"
                                    primary
                                    disabled={!userHasWritePermission}
                                    onClick={() => setShowNewCommentForm(true)}
                                >
                                    {t('common:new')}
                                </Button>
                            ) : (
                                <div>
                                    <Button data-testid="cancel-new-comment-button" onClick={onNewCommentCancel}>
                                        {t('common:cancel')}
                                    </Button>
                                    <Button
                                        data-testid="save-new-comment-button"
                                        disabled={!isValid || !isNewCommentDirty || isSubmitting}
                                        primary
                                        onClick={() => handleSubmit(onNewCommentSubmit)()}
                                    >
                                        {t('common:save')}
                                    </Button>
                                </div>
                            )}
                        </div>

                        {showNewCommentForm && (
                            <div style={{ margin: '10px 10px 4px 10px' }}>
                                <TextAreaController
                                    disabled={readOnly}
                                    control={control}
                                    placeholder={t('comments:addAComment')}
                                    className={styles.commentTextArea}
                                    name="content"
                                    data-testid="new-comment-textbox"
                                    required
                                    maxLength={300}
                                    validationTooltipPosition="left"
                                />
                            </div>
                        )}

                        <ChipList
                            className={cn(styles.chipListContainer)}
                            data-testid="panel-preview"
                            data={[
                                { text: t('comments:openCount', { count: commentCounts?.open }), value: 1 },
                                { text: t('comments:closedCount', { count: commentCounts?.closed }), value: 2 },
                                { text: t('comments:allCount', { count: commentCounts?.all }), value: 3 },
                            ]}
                            value={chipValue}
                            onChange={async (event) => {
                                if (event.value) {
                                    setChipValue(event.value as number);
                                }
                            }}
                            selection="single"
                        />
                    </div>
                    {(comments.length === 0 ||
                        (commentCounts?.open === 0 && chipValue === ChipValues.Open) ||
                        (commentCounts?.closed === 0 && chipValue === ChipValues.Closed)) && (
                        <div className="k-d-flex k-justify-content-center k-h-full">
                            <InfoField type="Info" message={t('comments:noCommentsAvailable')} layout="center" />
                        </div>
                    )}
                    <CommentsContext value={commentsContextValue}>
                        <div
                            className={styles.commentListContainer}
                            ref={commentsListRef}
                            onScroll={(e) => {
                                scrollTopsByChipRef.current[chipValue] = e.currentTarget.scrollTop;
                            }}
                        >
                            {getSortedComments().map((comment) => (
                                <Activity
                                    key={comment.id}
                                    mode={
                                        chipValue === ChipValues.All ||
                                        (chipValue === ChipValues.Open && comment.open) ||
                                        (chipValue === ChipValues.Closed && !comment.open)
                                            ? 'visible'
                                            : 'hidden'
                                    }
                                >
                                    <CommentCard
                                        comment={comment}
                                        onDirtyStateUpdated={onCommentDirtyStateUpdated}
                                        onUpdateComment={onUpdateComment}
                                        onOpenComment={onOpenComment}
                                        onCloseComment={onCloseComment}
                                        onDeleteComment={onDeleteComment}
                                        onCreateReply={onCreateCommentReply}
                                        onDeleteReply={onDeleteCommentReply}
                                        onUpdateReply={onUpdateCommentReply}
                                    />
                                </Activity>
                            ))}
                        </div>
                    </CommentsContext>
                </>
            )}
        </div>
    );
};

const CommentsSideBar = ({ parentId, ...rest }: CommentsSideBarProps) => (
    <CommentsSideBarInner key={parentId} parentId={parentId} {...rest} />
);

export default CommentsSideBar;
